import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save, StickyNote } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { DemoBanner } from "@/components/portal/DemoBanner";
import {
  listLoads,
  updateLoad,
  type LoadRecord,
  type LoadStatus,
} from "@/features/loads/repo";
import { useAuth } from "@/features/auth/useAuth";

const STATUS_LABEL: Record<LoadStatus, string> = {
  booked: "Booked",
  dispatched: "Dispatched",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Exception",
};

const SOURCE_LABEL: Record<LoadRecord["source"], string> = {
  quickmanage: "QuickManage",
  spreadsheet: "Spreadsheet",
  manual: "Manual overlay",
};

export default function InternalLoads() {
  const { user } = useAuth();
  const [loads, setLoads] = useState<LoadRecord[]>([]);
  const [selected, setSelected] = useState<LoadRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LoadStatus | "all">("all");
  const [draft, setDraft] = useState({
    internalNotes: "",
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const loadData = await listLoads(user);
      setLoads(loadData);
      setSelected((current) =>
        current ? loadData.find((load) => load.id === current.id) ?? loadData[0] ?? null : loadData[0] ?? null,
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load loads.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selected) return;
    setDraft({
      internalNotes: selected.internalNotes,
    });
  }, [selected]);

  const filtered = useMemo(
    () =>
      statusFilter === "all"
        ? loads
        : loads.filter((load) => load.status === statusFilter),
    [loads, statusFilter],
  );

  const overview = useMemo(
    () => ({
      active: loads.filter((load) => load.status !== "delivered").length,
      unassigned: loads.filter((load) => !load.driverName && !load.driverProfileId).length,
      exceptions: loads.filter((load) => load.status === "exception").length,
      missingDates: loads.filter((load) => !load.pickup || !load.delivery).length,
      equipmentWarnings: loads.filter((load) => load.equipmentWarnings.length > 0).length,
    }),
    [loads],
  );

  async function saveSelected() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await updateLoad(selected.id, {
        internalNotes: draft.internalNotes,
      }, {
        reference: selected.reference,
        origin: selected.origin,
        destination: selected.destination,
        source: selected.source,
        quickmanageId: selected.quickmanageId,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save load.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Seo title="Loads" noindex />
      <DemoBanner />

      <div>
        <h1 className="text-2xl font-bold">Dispatch control view</h1>
        <p className="mt-1 max-w-3xl text-sm text-brand-500">
          Review QuickManage and spreadsheet load data without changing the source
          systems. BB-owned notes save here as an internal overlay; operational
          changes such as driver assignment, carrier/equipment, and status remain
          read-only unless QuickManage write access is confirmed.
        </p>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-4 w-full max-w-xs">
        <Label htmlFor="load-status-filter">Filter loads</Label>
        <select
          id="load-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LoadStatus | "all")}
          className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
        >
          <option value="all">All loads ({loads.length})</option>
          {(Object.keys(STATUS_LABEL) as LoadStatus[]).map((status) => (
            <option key={status} value={status}>
              {STATUS_LABEL[status]} ({loads.filter((load) => load.status === status).length})
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <OverviewCard label="Active loads" value={overview.active} />
        <OverviewCard label="No driver shown" value={overview.unassigned} tone="warning" />
        <OverviewCard label="Exceptions" value={overview.exceptions} tone="danger" />
        <OverviewCard label="Missing dates" value={overview.missingDates} tone="warning" />
        <OverviewCard label="Fleetio warnings" value={overview.equipmentWarnings} tone="danger" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="grid gap-3 md:hidden">
          {loading && (
            <p className="rounded-lg border border-brand-100 bg-white p-4 text-sm text-brand-500">
              Loading loads...
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="rounded-lg border border-dashed border-brand-200 bg-white p-4 text-sm text-brand-500">
              No loads match this filter. Try another status or refresh after source systems update.
            </p>
          )}
          {!loading &&
            filtered.map((load) => (
              <button
                key={load.id}
                type="button"
                onClick={() => setSelected(load)}
                className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                  selected?.id === load.id
                    ? "border-accent-500 ring-2 ring-accent-500/10"
                    : "border-brand-100 hover:border-brand-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-brand-900">#{load.reference}</p>
                    <p className="mt-1 break-words text-sm text-brand-600">
                      {load.origin} &rarr; {load.destination}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Badge variant="accent">{STATUS_LABEL[load.status]}</Badge>
                    {load.equipmentWarnings.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-red-700">
                        <AlertTriangle className="h-3 w-3" /> Fleetio
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid gap-1 text-xs text-brand-500">
                  <span>Pickup: {load.pickup || "Missing"}</span>
                  <span>Driver: {load.driverName || "No driver shown"}</span>
                  <span>Source: {SOURCE_LABEL[load.source]}</span>
                </div>
              </button>
            ))}
        </div>

        <div className="hidden overflow-x-auto rounded-lg border border-brand-100 md:block lg:col-span-3">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-50 text-brand-600">
              <tr>
                <th className="px-4 py-2">Reference</th>
                <th className="px-4 py-2">Lane</th>
                <th className="px-4 py-2">Pickup</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-brand-500">
                    Loading loads...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-brand-500">
                    No loads match this filter. Try another status or refresh after source systems update.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((load) => (
                  <tr
                    key={load.id}
                    className={`cursor-pointer border-t border-brand-100 hover:bg-brand-50/80 ${
                      selected?.id === load.id ? "bg-accent-500/5" : ""
                    }`}
                    onClick={() => setSelected(load)}
                  >
                    <td className="px-4 py-2 font-medium">#{load.reference}</td>
                    <td className="px-4 py-2">
                      {load.origin} &rarr; {load.destination}
                    </td>
                    <td className="px-4 py-2">{load.pickup || "-"}</td>
                    <td className="px-4 py-2">
                      <Badge variant="accent">{STATUS_LABEL[load.status]}</Badge>
                    </td>
                    <td className="px-4 py-2">{load.driverName || "-"}</td>
                    <td className="px-4 py-2 text-brand-500">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{SOURCE_LABEL[load.source]}</span>
                        {load.equipmentWarnings.length > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-red-700">
                            <AlertTriangle className="h-3 w-3" /> Fleetio
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Card className="min-w-0 lg:col-span-2">
          <CardHeader>
            <CardTitle>{selected ? `#${selected.reference}` : "Select a load"}</CardTitle>
          </CardHeader>
          <CardContent>
            {!selected && (
              <p className="text-sm text-brand-500">Choose a row to review source data and add local notes.</p>
            )}
            {selected && (
              <div className="space-y-4">
                <p className="break-words text-sm text-brand-600">
                  {selected.origin} &rarr; {selected.destination}
                </p>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      Source-system fields below are informational. Update official
                      dispatch data in QuickManage/Samsara/Fleetio, then refresh this view.
                    </p>
                  </div>
                </div>

                {selected.equipmentWarnings.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                    <div className="flex gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-1">
                        <p className="font-semibold">Fleetio equipment warning</p>
                        {selected.equipmentWarnings.map((warning) => (
                          <p key={`${warning.fleetioUnit}-${warning.fleetioStatus}`}>
                            {warning.message}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <ReadOnlyField label="Status" value={STATUS_LABEL[selected.status]} />
                  <ReadOnlyField label="Driver" value={selected.driverName || "No driver shown"} />
                  <ReadOnlyField label="Carrier" value={selected.carrierName || "Not shown"} />
                  <ReadOnlyField label="Equipment" value={selected.equipmentUnit || "Not shown"} />
                  <ReadOnlyField label="Source" value={SOURCE_LABEL[selected.source]} />
                  <ReadOnlyField label="Pickup" value={selected.pickup || "Missing"} />
                  <ReadOnlyField label="Delivery" value={selected.delivery || "Missing"} />
                </div>

                <div>
                  <Label htmlFor="load-notes">
                    <span className="inline-flex items-center gap-1">
                      <StickyNote className="h-4 w-4" /> Internal notes
                    </span>
                  </Label>
                  <Textarea
                    id="load-notes"
                    className="mt-1"
                    value={draft.internalNotes}
                    disabled={saving}
                    onChange={(e) =>
                      setDraft((current) => ({ ...current, internalNotes: e.target.value }))
                    }
                  />
                </div>

                <Button variant="accent" className="w-full sm:w-auto" disabled={saving} onClick={() => void saveSelected()}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? "Saving..." : "Save local notes"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function OverviewCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "warning" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-brand-100 bg-white text-brand-900";

  return (
    <div className={`rounded-lg border px-4 py-3 ${toneClass}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-brand-50 px-3 py-2 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-400">{label}</p>
      <p className="mt-1 text-brand-800">{value}</p>
    </div>
  );
}
